import React from "react";

const AironAbout = () => {
  return (
    <div className="min-h-screen bg-black text-white px-4 py-2">

      {/* Header */}
      <header className="mb-2">
        <h1 className="text-xl lg:text-2xl font-bold flex items-center gap-3">
          <i className="fa-solid fa-wind text-cyan-400"></i>
          Airon — HVAC All Around Us
        </h1>
        <p className="mt-1 text-xs lg:text-sm text-white leading-relaxed">
          “HVAC is the invisible system that keeps us comfortable, productive,
          and healthy.”
        </p>
      </header>

      {/* Everyday Explanation */}
      <section className="mb-2">
        <p className="text-sm leading-relaxed text-white">
          As a common citizen in India, HVAC is quietly working around us every
          single day — even when we don’t notice it.
        </p>
        <p className="mt-1 text-lg lg:text-lg font-semibold text-cyan-300">
          Simply remember HVAC:
        </p>
        <p className="text-sm text-white">
          If air is being <strong>cooled</strong>, <strong>heated</strong>,
          <strong> cleaned</strong>, or <strong>moved</strong> — HVAC is involved.
        </p>
      </section>

      {/* Home */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-2 text-yellow-200">
          🏠 At Home
        </h2>
                <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>❄️ Split AC → cooling & humidity control</li>
          <li>🌬️ Exhaust fan → ventilation (HVAC)</li>
          <li>🔥 Geyser → heating</li>
          <li>🌡️ AC thermostat → temperature control</li>
          <li>🧹 Air purifier → indoor air quality (IAQ)</li>
        </ul>
        <p className="mt-1 text-sm text-white/80">
          AC smell, water leakage, uneven cooling? That’s an HVAC issue.
        </p>
      </section>

      {/* Office */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-2 text-yellow-200">
          🏢 Office / Workplace
        </h2>
        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>🌬️ Central AC ceiling vents</li>
          <li>❄️ Chillers in basements (hidden)</li>
          <li>🌡️ Too cold / too hot → HVAC imbalance</li>
          <li>🕒 Auto shutdown at night → BMS controlled</li>
        </ul>
        <p className="mt-1 text-sm text-white/80">
          “Office AC is never comfortable” = HVAC design problem.
        </p>
      </section>

      {/* Markets */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-2 text-yellow-200">
          🛒 Markets, Malls & Shops
        </h2>
        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>🏬 Mall cooling systems</li>
          <li>🧥 Air curtains at entrances</li>
          <li>🧊 Cold storage rooms</li>
          <li>🍦 Ice-cream freezers</li>
        </ul>
        <p className="mt-1 text-sm text-white/80">
          Mall feels cool in May heat = heavy-duty HVAC.
        </p>
      </section>

      {/* Public */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-2 text-yellow-200">
          🚆 Public Places
        </h2>
        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>🚇 Metro stations & trains</li>
          <li>🏥 Hospital ICUs & OTs</li>
          <li>🏫 Schools & coaching centers</li>
          <li>🎥 Cinema halls</li>
        </ul>
        <p className="mt-1 text-sm text-white/80">
          In hospitals, HVAC is life-saving — not a luxury.
        </p>
      </section>

      {/* Horeca */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-2 text-yellow-200">
          🍽️ HORECA
        </h2>
        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>🍳 Kitchen exhaust & fresh air</li>
          <li>❄️ Dining area AC</li>
          <li>🏨 Hotel room climate control</li>
          <li>🧊 Cold rooms for food</li>
        </ul>
        <p className="mt-1 text-sm text-white/80">
          Bad ventilation = smell, heat, health complaints.
        </p>
      </section>

      {/* Industry */}
      <section className="mb-2">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-2 text-yellow-200">
          🏭 Factories & Warehouses
        </h2>
        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>🏭 Manufacturing ventilation</li>
          <li>🧪 Clean rooms (pharma, electronics)</li>
          <li>❄️ Vaccine & food cold chains</li>
          <li>🌬️ Dust extraction systems</li>
        </ul>
        <p className="mt-1 text-sm text-white/80">
          Without HVAC, factories cannot legally or safely operate.
        </p>
      </section>

      {/* Environment */}
      <section className="mb-12">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-2 text-yellow-200">
          🌍 Environment & Health Impact
        </h2>
        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>😷 Poor ventilation → headaches & fatigue</li>
          <li>🌫️ Bad IAQ → asthma & allergies</li>
          <li>🔥 Heat waves → AC demand spikes</li>
          <li>⚡ Power cuts damage HVAC electronics</li>
        </ul>
      </section>

      {/* Airon Fit */}
      <section className="mb-12">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-2 text-yellow-200">
          <i className="fa-solid fa-brain text-cyan-400"></i>
          Where Airon (Think5Whys TaaP) Fits
        </h2>
        <ul className="space-y-2 text-sm [&>li]:text-white">
          <li>✍️ Technician training & diagnostics</li>
          <li>📊 Energy efficiency & sustainability scoring</li>
          <li>🧠 Root-cause analysis of comfort issues</li>
          <li>🏢 Digital twins of buildings</li>
          <li>👷 Skill certification & job matching</li>
          <li>🌱 Climate responsibility tracking</li>
        </ul>
      </section>

      {/* Positioning */}
      <section className="mb-12">
        <h2 className="text-lg lg:text-xl font-semibold flex items-center gap-2 mb-2 text-yellow-200">
          <i className="fa-solid fa-network-wired text-emerald-400"></i>
          Our Positioning
        </h2>
        <p className="text-sm leading-relaxed text-white">
          Think5Whys is not an HVAC platform.
          <br />
          It is a <strong>Climate Infrastructure Intelligence Platform</strong>.
        </p>
        <p className="mt-1 text-sm text-white/80">
          From HVAC skill crisis → intelligence-driven climate infrastructure
          ecosystem.
        </p>
      </section>

      {/* Final */}
      <footer className="border-t border-gray-800 pt-5">
        <p className="text-xs text-center text-white">
          HVAC is invisible — until it fails.
          <br />
          Airon + Think5Whys makes it intelligent, resilient, and future-ready.
        </p>
      </footer>

    </div>
  );
};

export default AironAbout;
